import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LANGUAGES, SupportedLanguage } from "@/i18n";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

export function LanguageSelector() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);

  const currentLang = SUPPORTED_LANGUAGES.find(
    (l) => l.code === i18n.language?.substring(0, 2)
  ) || SUPPORTED_LANGUAGES[0];

  const changeLanguage = async (code: SupportedLanguage) => {
    i18n.changeLanguage(code);
    localStorage.setItem("guia-essencial-language", code);

    // Persist to profile if authenticated
    if (user) {
      setSaving(true);
      await supabase
        .from("profiles")
        .update({ preferred_language: code })
        .eq("user_id", user.id);
      setSaving(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-10 gap-2 px-3 text-base"
          disabled={saving}
        >
          <Globe className="h-5 w-5" />
          <span className="text-lg">{currentLang.flag}</span>
          <span className="hidden sm:inline">{currentLang.nativeLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className="cursor-pointer gap-3 text-base py-2.5"
          >
            <span className="text-lg">{lang.flag}</span>
            <span className="flex-1">{lang.nativeLabel}</span>
            {currentLang.code === lang.code && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
